import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Save, X, Plus, Flag } from 'lucide-react';

interface LeadDurum {
    statusID: number;
    statusAdi: string;
    aktifMi: boolean;
    takip: boolean;
}

const emptyForm = { statusAdi: '', aktifMi: true, takip: false };

const LeadStatusAyarlari: React.FC = () => {
    const [durumlar, setDurumlar] = useState<LeadDurum[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected] = useState<LeadDurum | null>(null);
    const [form, setForm] = useState(emptyForm);

    const fetchDurumlar = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/LeadDurumlari');
            setDurumlar(res.data);
        } catch {
            toast.error('Durumlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDurumlar(); }, []);

    const openAdd = () => {
        setSelected(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (d: LeadDurum) => {
        setSelected(d);
        setForm({ statusAdi: d.statusAdi, aktifMi: d.aktifMi, takip: d.takip });
        setShowModal(true);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.statusAdi.trim()) { toast.warn('Durum adı zorunludur'); return; }
        setSaving(true);
        try {
            if (selected) {
                await axiosInstance.put(`/api/LeadDurumlari/${selected.statusID}`, {
                    statusID: selected.statusID,
                    ...form,
                });
                toast.success('Durum güncellendi');
            } else {
                await axiosInstance.post('/api/LeadDurumlari', form);
                toast.success('Durum eklendi');
            }
            setShowModal(false);
            fetchDurumlar();
        } catch {
            toast.error('Kaydedilemedi');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Bu durumu silmek istiyor musunuz?')) return;
        try {
            await axiosInstance.delete(`/api/LeadDurumlari/${id}`);
            toast.success('Durum silindi');
            fetchDurumlar();
        } catch {
            toast.error('Silinemedi');
        }
    };

    const kanbanDurumlar = durumlar.filter(d => d.takip);
    const listeDurumlar = durumlar.filter(d => !d.takip);

    return (
        <div style={{ padding: '24px', background: '#ffffff', fontFamily: 'Inter, sans-serif' }}>
            {/* Başlık */}
            <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#111827' }}>Potansiyel Müşteri Durumları</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>Lead takip sürecinde kullanılacak durum tanımlamaları</p>
                </div>
                <button
                    onClick={openAdd}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; }}
                    style={{
                        background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '9px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 7,
                        boxShadow: '0 1px 3px rgba(205,105,6,0.3)',
                    }}
                >
                    <Plus size={15} /> Yeni Durum
                </button>
            </div>

            {/* Bilgi kartları */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
                {[
                    { label: 'Toplam Durum', value: durumlar.length, icon: 'fas fa-list', color: '#111827', bg: '#f3f4f6' },
                    { label: 'Kanban (Takip)', value: kanbanDurumlar.length, icon: 'fas fa-columns', color: '#cd6906', bg: '#fef3e2' },
                    { label: 'Liste (Takipsiz)', value: listeDurumlar.length, icon: 'fas fa-table', color: '#2563eb', bg: '#eff6ff' },
                ].map(c => (
                    <div key={c.label} style={{
                        background: '#fff', borderRadius: 12, padding: '18px 22px',
                        border: '1px solid #e5e7eb',
                        display: 'flex', alignItems: 'center', gap: 16,
                    }}>
                        <div style={{
                            width: 44, height: 44, borderRadius: 10, background: c.bg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className={c.icon} style={{ color: c.color, fontSize: 18 }}></i>
                        </div>
                        <div>
                            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>{c.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tablo */}
            <div style={{
                background: 'rgba(249,250,251,0.5)', borderRadius: 12, border: '1px solid #e5e7eb',
                overflow: 'hidden', marginTop: 8,
            }}>
                <div style={{
                    padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: 'rgba(243,244,246,0.5)',
                    display: 'flex', alignItems: 'center', gap: 8,
                }}>
                    <Flag size={13} color="#6b7280" />
                    <span style={{ fontWeight: 700, color: '#6b7280', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Durum Listesi</span>
                </div>

                {loading ? (
                    <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
                        <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i>
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                            <tr>
                                {['#', 'Durum Adı', 'Aktif Mi', 'Takip (Kanban)', 'İşlemler'].map((h, i) => (
                                    <th key={h} style={{
                                        padding: '10px 16px', textAlign: i >= 2 ? 'center' : 'left',
                                        fontWeight: 700, color: '#6b7280', borderBottom: '1px solid #e5e7eb',
                                        fontSize: '0.72rem', textTransform: 'uppercase' as const, letterSpacing: '0.06em',
                                        whiteSpace: 'nowrap' as const, background: 'rgba(243,244,246,0.5)',
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {durumlar.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                                        Henüz durum tanımlanmamış
                                    </td>
                                </tr>
                            ) : durumlar.map((d, i) => (
                                <tr key={d.statusID}
                                    style={{ borderBottom: '1px solid #e5e7eb', transition: 'background 0.1s' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#fff')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                    <td style={{ padding: '12px 16px', color: '#9ca3af', fontWeight: 600, fontSize: '0.85rem' }}>{d.statusID}</td>
                                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}>{d.statusAdi}</td>
                                    <td style={{ padding: '12px 18px', textAlign: 'center' }}>
                                        <span style={{
                                            display: 'inline-block', padding: '3px 12px', borderRadius: 20, fontSize: '0.75rem',
                                            fontWeight: 700,
                                            background: d.aktifMi ? '#dcfce7' : '#fee2e2',
                                            color: d.aktifMi ? '#166534' : '#991b1b',
                                        }}>
                                            {d.aktifMi ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 18px', textAlign: 'center' }}>
                                        <span style={{
                                            display: 'inline-block', padding: '3px 12px', borderRadius: 20, fontSize: '0.75rem',
                                            fontWeight: 700,
                                            background: d.takip ? '#fef3c7' : '#f1f5f9',
                                            color: d.takip ? '#92400e' : '#64748b',
                                        }}>
                                            {d.takip ? 'Kanban' : 'Liste'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 18px', textAlign: 'center' }}>
                                        <div style={{ display: 'inline-flex', gap: 8 }}>
                                            <button
                                                onClick={() => openEdit(d)}
                                                style={{
                                            background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 7,
                                                    padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem',
                                                    display: 'flex', alignItems: 'center', gap: 5,
                                                }}
                                            >
                                                <i className="fas fa-pen"></i> Düzenle
                                            </button>
                                            <button
                                                onClick={() => handleDelete(d.statusID)}
                                                style={{
                                            background: '#fff1f2', color: '#f43f5e', border: 'none', borderRadius: 7,
                                                    padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem',
                                                    display: 'flex', alignItems: 'center', gap: 5,
                                                }}
                                            >
                                                <i className="fas fa-trash"></i> Sil
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Açıklama */}
            <div style={{
                marginTop: 20, background: '#fff7ed', border: '1px solid #fed7aa',
                borderRadius: 10, padding: '14px 20px', display: 'flex', gap: 12, alignItems: 'flex-start',
            }}>
                <i className="fas fa-info-circle" style={{ color: '#e67e22', marginTop: 2, flexShrink: 0 }}></i>
                <div style={{ fontSize: '0.82rem', color: '#92400e', lineHeight: 1.6 }}>
                    <strong>Takip (Kanban):</strong> Bu seçenek <em>açık</em> olan durumlar, Lead Takip sayfasında kanban kolonları olarak görüntülenir ve sürükle-bırak ile yönetilebilir.
                    <br />
                    <strong>Takip (Liste):</strong> Bu seçenek <em>kapalı</em> olan durumlar, "Tüm Potansiyel Müşteriler" tablosunda listelenir.
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} onClick={() => setShowModal(false)}>
                    <div style={{
                        background: '#fff', borderRadius: 14,
                        width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                        display: 'flex', flexDirection: 'column',
                    }} onClick={e => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div style={{
                            padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <h3 style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: '1.1rem' }}>
                                {selected ? 'Durumu Düzenle' : 'Yeni Durum Ekle'}
                            </h3>
                            <button onClick={() => setShowModal(false)}
                                onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = '#f3f4f6'; b.style.color = '#374151'; }}
                                onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.background = 'none'; b.style.color = '#9ca3af'; }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '6px', borderRadius: 6, display: 'flex', transition: 'all 0.15s' }}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSave}>
                            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                                        Durum Adı *
                                    </label>
                                    <input
                                        type="text"
                                        value={form.statusAdi}
                                        onChange={e => setForm(f => ({ ...f, statusAdi: e.target.value }))}
                                        onFocus={e => { e.currentTarget.style.borderColor = '#111827'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; }}
                                        placeholder="Ör: Arandı, Beklemede..."
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: 8,
                                            border: '1px solid #d1d5db', fontSize: '0.875rem',
                                            outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s',
                                        }}
                                        autoFocus
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    {/* Aktif Mi */}
                                    <label style={{
                                        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                                        background: form.aktifMi ? '#f0fdf4' : '#f9fafb',
                                        border: `1.5px solid ${form.aktifMi ? '#bbf7d0' : '#e5e7eb'}`,
                                        borderRadius: 10, padding: '12px 14px',
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={form.aktifMi}
                                            onChange={e => setForm(f => ({ ...f, aktifMi: e.target.checked }))}
                                            style={{ width: 16, height: 16, accentColor: '#111827' }}
                                        />
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827' }}>Aktif Mi</div>
                                            <div style={{ fontSize: '0.73rem', color: '#6b7280', marginTop: 1 }}>Kullanımda mı?</div>
                                        </div>
                                    </label>

                                    {/* Takip */}
                                    <label style={{
                                        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                                        background: form.takip ? '#fef3e2' : '#f9fafb',
                                        border: `1.5px solid ${form.takip ? '#fcd9a0' : '#e5e7eb'}`,
                                        borderRadius: 10, padding: '12px 14px',
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={form.takip}
                                            onChange={e => setForm(f => ({ ...f, takip: e.target.checked }))}
                                            style={{ width: 16, height: 16, accentColor: '#cd6906' }}
                                        />
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827' }}>Takip (Kanban)</div>
                                            <div style={{ fontSize: '0.73rem', color: '#6b7280', marginTop: 1 }}>Kanban'da görünsün</div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '16px 24px', borderTop: '1px solid #f3f4f6',
                                background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)',
                                display: 'flex', justifyContent: 'flex-end', gap: 10,
                                borderBottomLeftRadius: 14, borderBottomRightRadius: 14,
                            }}>
                                <button type="button" onClick={() => setShowModal(false)}
                                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
                                    style={{
                                        padding: '9px 18px', border: '1px solid #d1d5db', borderRadius: 8,
                                        background: '#fff', color: '#374151', fontWeight: 500, cursor: 'pointer',
                                        fontSize: '0.875rem', transition: 'background 0.15s',
                                    }}>
                                    İptal
                                </button>
                                <button type="submit" disabled={saving}
                                    onMouseEnter={e => { if (!saving) (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; }}
                                    onMouseLeave={e => { if (!saving) (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; }}
                                    style={{
                                        padding: '9px 20px', background: '#cd6906', color: '#fff', border: 'none',
                                        borderRadius: 8, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                                        opacity: saving ? 0.7 : 1, fontSize: '0.875rem',
                                        display: 'flex', alignItems: 'center', gap: 7,
                                        boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'background 0.15s',
                                    }}>
                                    {saving
                                        ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 4 }}></i>Kaydediliyor</>
                                        : <><Save size={15} />Kaydet</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadStatusAyarlari;
